import { INDIA_STATE_DISTRICTS } from '@/data/state-districts';
import { INDIA_STATES_TAMIL } from '@/data/states';
import { EventBookingDetails } from '@/modals/event-details/eventBookingDetails';
import { Component, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { Subscription, fromEvent, debounceTime, takeUntil, Subject } from 'rxjs';

@Component({
  selector: 'app-feedback',
  templateUrl: './feedback.component.html',
  styleUrl: './feedback.component.scss'
})
export class FeedbackComponent {
  destroy$: Subject<boolean> = new Subject<boolean>();
  @ViewChild('pageHeader', { static: true })
  pageHeader!: ElementRef<HTMLElement>;

  @ViewChild('bgWrapper', { static: true })
  bgWrapper!: ElementRef<HTMLElement>;

  statesList = INDIA_STATES_TAMIL;
  districtList: any[] = [];
  currentYear = new Date().getFullYear();
  resizeSub?: Subscription;
  feedbackForm!: FormGroup;
  submitted = false;
  eventDetails: EventBookingDetails[] = [];

  constructor(
    private renderer: Renderer2,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private appService: AppService,
    private spinner: NgxSpinnerService
  ) { }

  ngOnInit() {

    this.eventDetailsGetAll();


    this.feedbackForm = this.fb.group({
      feedbackDetailsSeq: [0],
      eventId: ['', Validators.required],
      name: ['', Validators.required],
      state: [],
      city: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],

      overallExperience: ['', Validators.required],

      mostLiked: [[], Validators.required],

      improvements: [[], Validators.required],

      bestTeamOrSection: [[], Validators.required],

      willParticipateAgain: ['', Validators.required],

      additionalComments: ['']
    });


    this.feedbackForm.get('state')?.valueChanges.subscribe((stateValue) => {
      this.loadDistricts(stateValue);
    });
  }

  getTamilError(field: string): string {
    const messages: any = {
      name: 'பெயர் கட்டாயம்',
      city: 'நகரம் கட்டாயம்',
      email: 'சரியான மின்னஞ்சல் முகவரி உள்ளிடவும்',
      overallExperience: 'மொத்த அனுபவத்தை தேர்வு செய்யவும்',
      mostLiked: 'குறைந்தது ஒரு அம்சத்தை தேர்வு செய்யவும்',
      improvements: 'குறைந்தது ஒரு மேம்பாட்டை தேர்வு செய்யவும்',
      bestTeamOrSection: 'ஒரு குழுவை தேர்வு செய்யவும்',
      willParticipateAgain: 'தேர்வு செய்யவும்'
    };
    return messages[field] || 'இந்த புலம் தவறாக உள்ளது';
  }


  eventDetailsGetAll() {
    this.spinner.show();
    this.appService.eventDetailsGetAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        const today = new Date().setHours(0, 0, 0, 0);
        const sorted = data
          .sort((a, b) => {
            const aIsFuture = a.startDate >= today;
            const bIsFuture = b.startDate >= today;
            if (aIsFuture && !bIsFuture) return -1;
            if (!aIsFuture && bIsFuture) return 1;
            return a.startDate - b.startDate;
          });
        this.eventDetails = sorted;
        console.log(`all events::`, this.eventDetails);
        this.spinner.hide();
      }, (error: any) => {
        this.spinner.hide()
        this.toastr.error(error?.error?.message || "Something Went Wrong, Please Try Again")
      });
  }

  formattedDate(epoch: number): string {
    const date = new Date(epoch);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  ngAfterViewInit() {
    this.adjustContentMargin();
    this.resizeSub = fromEvent(window, 'resize')
      .pipe(debounceTime(120))
      .subscribe(() => this.adjustContentMargin());
  }

  adjustContentMargin() {
    const headerEl = this.pageHeader?.nativeElement;
    const wrapperEl = this.bgWrapper?.nativeElement;
    if (headerEl && wrapperEl) {
      const height = headerEl.offsetHeight;
      this.renderer.setStyle(wrapperEl, 'margin-top', `${height}px`);
    }
  }

  // ------------------- HELPERS -------------------
  loadDistricts(stateValue: string) {
    this.districtList = INDIA_STATE_DISTRICTS[stateValue] || [];
  }

  allowOnlyNumbers(event: any) {
    event.target.value = event.target.value.replace(/[^0-9]/g, '');
  }

  onHeaderImageLoad(): void {
    // Small timeout ensures browser layout has stabilized (optional)
    setTimeout(() => {
      this.adjustContentMargin();
    }, 20);
  }

  onCheckboxChange(event: any, controlName: string) {
    const control = this.feedbackForm.get(controlName);

    if (!control) return;

    const selectedValues = control.value as string[];

    if (event.target.checked) {
      if (!selectedValues.includes(event.target.value)) {
        selectedValues.push(event.target.value);
      }
    } else {
      const index = selectedValues.indexOf(event.target.value);
      if (index > -1) {
        selectedValues.splice(index, 1);
      }
    }

    control.setValue(selectedValues);
  }

  submitForm() {
    this.submitted = true;

    // 1️⃣ Validate form
    if (this.feedbackForm.invalid) {
      this.toastr.error(
        'தயவுசெய்து தேவையான அனைத்து புலங்களையும் சரியாக நிரப்பவும்',
        'பிழை'
      );
      this.focusFirstInvalidControl();
      return;
    }

    // 2️⃣ Prepare payload
    const formValue = this.feedbackForm.value;
    const payload = {
      ...formValue,
      mostLiked: Array.isArray(formValue.mostLiked) ? formValue.mostLiked.join(',') : formValue.mostLiked,
      improvements: Array.isArray(formValue.improvements) ? formValue.improvements.join(',') : formValue.improvements,
      bestTeamOrSection: Array.isArray(formValue.bestTeamOrSection) ? formValue.bestTeamOrSection.join(',') : formValue.bestTeamOrSection,
    };

    // 3️⃣ Confirmation before save
    Swal.fire({
      title: 'உறுதிப்படுத்தவும்',
      text: 'நீங்கள் மதிப்பீட்டை சமர்ப்பிக்க விரும்புகிறீர்களா?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'ஆம், சமர்ப்பிக்கவும்',
      cancelButtonText: 'ரத்துசெய்'
    }).then((result) => {
      if (result.isConfirmed) {
        // 4️⃣ Save feedback
        this.appService.saveFeedbacks(payload).subscribe({
          next: () => {
            this.toastr.success('மதிப்பீடு வெற்றிகரமாக சேமிக்கப்பட்டது!');
            this.feedbackForm.reset();
            this.submitted = false;
            window.location.reload()
          },
          error: (err) => {
            console.error('Error saving feedback', err);
            this.toastr.error('மதிப்பீட்டை சேமிக்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.');
          }
        });
      }
    });
  }


  private focusFirstInvalidControl() {
    const firstInvalidControl: HTMLElement | null = document.querySelector(
      '.ng-invalid[formControlName]'
    );

    if (firstInvalidControl) {
      firstInvalidControl.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      firstInvalidControl.focus();
    }
  }

  ngOnDestroy() {
    this.resizeSub?.unsubscribe();
  }

}
